import React, { useState, useEffect } from 'react';
import { Grid3X3, Search, CheckCircle, AlertCircle } from 'lucide-react';

const CategoriesStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);

  // Construction categories with icons and descriptions
  const constructionCategories = [
    {
      id: "cement_concrete",
      name: "Cement & Concrete",
      icon: "🏗️",
      description: "All types of cement, concrete, and related materials",
      subcategories: ["OPC Cement", "PPC Cement", "Ready Mix Concrete", "Concrete Blocks"]
    },
    {
      id: "steel_iron",
      name: "Steel & Iron",
      icon: "🔩",
      description: "TMT bars, structural steel, pipes, and metal products",
      subcategories: ["TMT Bars", "Steel Pipes", "Structural Steel", "Wire Mesh"]
    },
    {
      id: "bricks_blocks",
      name: "Bricks, Blocks & AAC",
      icon: "🧱",
      description: "Clay bricks, fly ash bricks, AAC blocks, and concrete blocks",
      subcategories: ["Red Clay Bricks", "Fly Ash Bricks", "AAC Blocks", "Hollow Blocks"]
    },
    {
      id: "sand_aggregates",
      name: "Sand, Aggregates & Soil",
      icon: "⛰️",
      description: "River sand, M-sand, aggregates, and filling materials",
      subcategories: ["River Sand", "M-Sand", "20mm Aggregate", "Stone Dust"]
    },
    {
      id: "paints_coatings",
      name: "Paints & Coatings",
      icon: "🎨",
      description: "Interior/exterior paints, primers, and waterproofing",
      subcategories: ["Interior Emulsion", "Exterior Emulsion", "Primers", "Waterproofing"]
    },
    {
      id: "tiles_marble",
      name: "Tiles, Marble & Granite",
      icon: "🏛️",
      description: "Vitrified tiles, marble slabs, granite, and stone cladding",
      subcategories: ["Vitrified Tiles", "Ceramic Tiles", "Marble Slabs", "Granite Slabs"]
    },
    {
      id: "plywood_boards",
      name: "Plywood & Boards",
      icon: "🪵",
      description: "Commercial plywood, MDF, particle boards, and laminates",
      subcategories: ["Commercial Plywood", "Marine Plywood", "MDF Boards", "Laminates"]
    },
    {
      id: "electrical_lighting",
      name: "Electrical & Lighting",
      icon: "💡",
      description: "Wires, cables, switches, lighting, and electrical accessories",
      subcategories: ["Wires & Cables", "Switches & Sockets", "LED Lights", "Ceiling Fans"]
    },
    {
      id: "plumbing_sanitary",
      name: "Plumbing & Sanitary",
      icon: "🚿",
      description: "Pipes, fittings, sanitary ware, and bathroom accessories",
      subcategories: ["PVC Pipes", "CPVC Pipes", "Sanitary Ware", "Bathroom Fittings"]
    },
    {
      id: "doors_windows",
      name: "Doors & Windows",
      icon: "🚪",
      description: "Wooden doors, UPVC windows, glass doors, and hardware",
      subcategories: ["Wooden Doors", "UPVC Windows", "Aluminium Windows", "Door Hardware"]
    },
    {
      id: "equipment_tools",
      name: "Construction Equipment",
      icon: "🔨",
      description: "Power tools, hand tools, and construction machinery",
      subcategories: ["Power Tools", "Hand Tools", "Concrete Mixers", "Scaffolding"]
    },
    {
      id: "roofing_insulation",
      name: "Roofing & Insulation",
      icon: "🏠",
      description: "Roofing sheets, tiles, insulation, and false ceiling",
      subcategories: ["Metal Sheets", "Roofing Tiles", "Insulation", "False Ceiling"]
    }
  ];

  useEffect(() => {
    setCategories(constructionCategories);
  }, []);

  const handleCategoryToggle = (categoryId) => {
    const currentSelected = formData.selectedCategories || [];
    let newSelected;

    if (currentSelected.includes(categoryId)) {
      // Remove category
      newSelected = currentSelected.filter(id => id !== categoryId);
    } else {
      // Add category (max 5)
      if (currentSelected.length >= 5) {
        setErrors(prev => ({
          ...prev,
          selectedCategories: 'Maximum 5 categories allowed'
        }));
        return;
      }
      newSelected = [...currentSelected, categoryId];
    }

    updateFormData({ selectedCategories: newSelected });
    
    // Clear errors when user makes selection
    if (newSelected.length > 0 && errors.selectedCategories) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.selectedCategories;
        return newErrors;
      });
    }
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedCount = formData.selectedCategories?.length || 0;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Select Your Business Categories</h2>
        <p className="text-gray-600">Choose up to 5 categories that best describe your construction business</p>
      </div>

      {/* Search and Selection Info */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex items-center space-x-4">
          <div className={`text-sm font-medium ${selectedCount > 5 ? 'text-red-600' : 'text-gray-600'}`}>
            {selectedCount}/5 selected
          </div>
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={() => updateFormData({ selectedCategories: [] })}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {errors.selectedCategories && (
        <div className="flex items-center text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 mr-2" />
          {errors.selectedCategories}
        </div>
      )}

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.map((category) => {
          const isSelected = formData.selectedCategories?.includes(category.id);
          const isDisabled = !isSelected && selectedCount >= 5;
          
          return (
            <div
              key={category.id}
              className={`
                relative border-2 rounded-lg p-6 cursor-pointer transition-all duration-200
                ${isSelected 
                  ? 'border-blue-500 bg-blue-50 shadow-md' 
                  : isDisabled
                    ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }
              `}
              onClick={() => !isDisabled && handleCategoryToggle(category.id)}
            >
              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute top-3 right-3">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
              )}
              
              {/* Category Icon */}
              <div className="text-4xl mb-4">{category.icon}</div>
              
              {/* Category Info */}
              <div>
                <h3 className={`text-lg font-semibold mb-2 ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                  {category.name}
                </h3>
                <p className={`text-sm mb-4 ${isSelected ? 'text-blue-700' : 'text-gray-600'}`}>
                  {category.description}
                </p>
                
                {/* Subcategories Preview */}
                <div className="space-y-1">
                  <p className={`text-xs font-medium ${isSelected ? 'text-blue-800' : 'text-gray-500'}`}>
                    Popular items:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {category.subcategories.slice(0, 3).map((sub, index) => (
                      <span
                        key={index}
                        className={`
                          text-xs px-2 py-1 rounded-full
                          ${isSelected 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-gray-100 text-gray-600'
                          }
                        `}
                      >
                        {sub}
                      </span>
                    ))}
                    {category.subcategories.length > 3 && (
                      <span className={`text-xs ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                        +{category.subcategories.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected Categories Summary */}
      {selectedCount > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-green-900 mb-3 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            Selected Categories ({selectedCount})
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {formData.selectedCategories?.map(categoryId => {
              const category = categories.find(cat => cat.id === categoryId);
              return category ? (
                <div key={categoryId} className="flex items-center justify-between bg-white rounded-lg p-3 border border-green-200">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{category.icon}</span>
                    <span className="font-medium text-gray-900">{category.name}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCategoryToggle(categoryId)}
                    className="text-red-500 hover:text-red-700"
                    title="Remove category"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : null;
            })}
          </div>
        </div>
      )}

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Grid3X3 className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Category Selection Tips
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Select categories that represent your main business areas</li>
                <li>You can choose up to 5 categories to maximize your reach</li>
                <li>Categories help customers find your products more easily</li>
                <li>You'll provide specific details about each category in the next step</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesStep;
